import { Modal } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import type { ChannelManager } from 'app/typings/channelManagers';

import styles from './AutoInventoryModal.module.scss';
import { StepAutoInventory } from './_steps/StepAutoInventory/StepAutoInventory';
import { StepAutoInventoryConfig } from './_steps/StepAutoInventoryConfig/StepAutoInventoryConfig';
import { StepChannelManager } from './_steps/StepChannelManager/StepChannelManager';
import { StepIntro } from './_steps/StepIntro/StepIntro';

type AutoInventoryStep =
  | 'intro'
  | 'channelManager'
  | 'autoInventory'
  | 'autoInventoryConfig';

const MODAL_TITLE: Record<AutoInventoryStep, string | null> = {
  intro: '',
  channelManager: '',
  autoInventory: '',
  autoInventoryConfig: 'Auto inventory',
};

const MODAL_WIDTH: Record<AutoInventoryStep, number> = {
  intro: 848,
  channelManager: 496,
  autoInventory: 496,
  autoInventoryConfig: 684,
};

type AutoInventoryModalProps = {
  isOpen: boolean;
  hotelId: number;
  onClose: () => void;
  onConfirmChannelManager: (channelManager: ChannelManager) => void;
};

export const AutoInventoryModal = ({
  isOpen,
  hotelId,
  onClose,
  onConfirmChannelManager,
}: AutoInventoryModalProps) => {
  const { hotelInfo } = useContext(HotelContext);
  const hasAutoInventoryActivated = hotelInfo?.stockScrapperEnabled ?? false;

  const defaultStep: AutoInventoryStep = useMemo(() => {
    return hasAutoInventoryActivated ? 'autoInventoryConfig' : 'intro';
  }, [hasAutoInventoryActivated]);

  const [step, setStep] = useState<AutoInventoryStep>(defaultStep);

  useEffect(() => {
    setStep(defaultStep);
  }, [defaultStep]);

  const handleClose = () => {
    onClose();
    setStep(defaultStep);
  };

  const renderStep = () => {
    switch (step) {
      case 'intro':
        return (
          <StepIntro
            onSelectChannelManager={() => setStep('channelManager')}
            onSelectBookingCom={() => setStep('autoInventory')}
          />
        );
      case 'channelManager':
        return <StepChannelManager onClose={handleClose} />;
      case 'autoInventory':
        return (
          <StepAutoInventory
            hotelId={hotelId}
            onClose={handleClose}
            onStartSync={() => setStep('autoInventoryConfig')}
          />
        );

      case 'autoInventoryConfig':
        return <StepAutoInventoryConfig handleClose={handleClose} />;
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      title={MODAL_TITLE[step]}
      footer={null}
      destroyOnClose
      centered
      width={MODAL_WIDTH[step]}
      wrapClassName={styles.autoInventoryModal}
    >
      {renderStep()}
    </Modal>
  );
};
